import { Minus, Plus, Trash2, X, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { Link } from 'react-router-dom';
import { cartItemToAnalytics, trackEcommerce } from '@/utils/analytics';

export function CartSidebar() {
  const { state, dispatch } = useCart();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(price);
  };

  const updateQuantity = (productId: string, presentation: string, quantity: number) => {
    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: { productId, presentation, quantity }
    });
  };

  const removeItem = (productId: string, presentation: string) => {
    const item = state.items.find(
      (entry) => entry.productId === productId && entry.presentation === presentation,
    );
    if (item) trackEcommerce('remove_from_cart', [cartItemToAnalytics(item)]);
    dispatch({
      type: 'REMOVE_ITEM',
      payload: { productId, presentation }
    });
  };

  const changeQuantity = (productId: string, presentation: string, nextQuantity: number) => {
    const item = state.items.find(
      (entry) => entry.productId === productId && entry.presentation === presentation,
    );
    if (item) {
      const delta = nextQuantity - item.quantity;
      if (delta > 0) trackEcommerce('add_to_cart', [cartItemToAnalytics(item, delta)]);
      if (delta < 0) trackEcommerce('remove_from_cart', [cartItemToAnalytics(item, Math.abs(delta))]);
    }
    updateQuantity(productId, presentation, nextQuantity);
  };

  if (!state.isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:z-40">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/50 lg:bg-transparent"
        onClick={() => dispatch({ type: 'CLOSE_CART' })}
      />

      {/* Sidebar */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-card border-l border-border shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold flex items-center">
            <ShoppingBag className="w-5 h-5 mr-2" />
            Tu Carrito
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => dispatch({ type: 'CLOSE_CART' })}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {state.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <ShoppingBag className="w-16 h-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Tu carrito está vacío</h3>
              <p className="text-muted-foreground mb-6">
                Agrega algunos productos naturales para comenzar
              </p>
              <Button 
                asChild
                onClick={() => dispatch({ type: 'CLOSE_CART' })}
              >
                <Link to="/tienda">
                  Explorar Productos
                </Link>
              </Button>
            </div>
          ) : (
            <div className="h-full flex flex-col">
              {/* Items List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {state.items.map((item) => (
                  <div key={`${item.productId}-${item.presentation}`} className="bg-muted/30 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      {/* Product Image */}
                      <div className="w-16 h-16 bg-muted rounded-lg flex-shrink-0 overflow-hidden">
                        {item.image ? (
                          <img 
                            src={item.image} 
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-primary/20 flex items-center justify-center">
                            <span className="text-xs text-primary font-medium">
                              {item.name.charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm truncate">{item.name}</h4>
                        <p className="text-xs text-muted-foreground">{item.presentation}</p>
                        <p className="text-sm font-medium text-primary mt-1">
                          {formatPrice(item.unitPrice)}
                        </p>
                      </div>

                      {/* Remove Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(item.productId, item.presentation)}
                        className="text-destructive hover:text-destructive"
                        aria-label={`Eliminar ${item.name} del carrito`}
                      >
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </Button>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => changeQuantity(item.productId, item.presentation, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          className="w-8 h-8 p-0"
                          aria-label={`Disminuir cantidad de ${item.name}`}
                        >
                          <Minus className="w-3 h-3" aria-hidden="true" />
                        </Button>
                        
                        <span className="w-12 text-center font-medium">
                          {item.quantity}
                        </span>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => changeQuantity(item.productId, item.presentation, item.quantity + 1)}
                          className="w-8 h-8 p-0"
                          aria-label={`Aumentar cantidad de ${item.name}`}
                        >
                          <Plus className="w-3 h-3" aria-hidden="true" />
                        </Button>
                      </div>

                      <p className="font-semibold text-primary">
                        {formatPrice(item.subtotal)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="border-t border-border p-4 space-y-4 bg-muted/20">
                {/* Total */}
                <div className="flex justify-between items-center text-lg font-semibold">
                  <span>Total:</span>
                  <span className="text-primary">{formatPrice(state.total)}</span>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <Button 
                    asChild 
                    className="w-full bg-primary hover:bg-primary-light"
                    onClick={() => dispatch({ type: 'CLOSE_CART' })}
                  >
                    <Link to="/pedido/resumen">
                      Ir al Resumen
                    </Link>
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="w-full"
                    asChild
                    onClick={() => dispatch({ type: 'CLOSE_CART' })}
                  >
                    <Link to="/tienda">
                      Seguir Comprando
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}